import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'localization';
import { useContentPermissions } from '../../../hooks/useContentPermissions';
import './ContentSharingDropdownMolecule.scss';

export interface ContentSharingDropdownMoleculeProps {
  contentType: string;
  contentId: string;
  className?: string;
}

const ContentSharingDropdownMolecule: React.FC<ContentSharingDropdownMoleculeProps> = ({
  contentType,
  contentId,
  className = '',
}) => {
  const { t } = useTranslation();
  const { permissions, loading, error, update, isUpdating } =
    useContentPermissions(contentType, contentId);

  const [open, setOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
    setSaveError(null);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
    }
  }, []);

  const handleSharingChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!permissions) return;

      setSaveError(null);
      const success = await update({
        allow_sharing: e.target.name === 'allow_sharing' ? e.target.checked : permissions.allow_sharing,
        allow_cloning: e.target.name === 'allow_cloning' ? e.target.checked : permissions.allow_cloning,
        attribution_required: permissions.attribution_required,
      });

      if (!success) {
        setSaveError(t('marketplace.channelManage.sharing.saveError'));
      }
    },
    [permissions, update, t]
  );

  const classes = ['marketplace-content-sharing', className].filter(Boolean).join(' ');

  if (loading) {
    return (
      <div className={classes}>
        <i className="pi pi-spin pi-spinner marketplace-content-sharing__spinner" />
      </div>
    );
  }

  if (error || !permissions) {
    return null;
  }

  return (
    <div className={classes} ref={containerRef} onKeyDown={handleKeyDown}>
      <button
        type="button"
        className="marketplace-content-sharing__trigger"
        onClick={handleToggle}
        aria-expanded={open}
        aria-haspopup="true"
        title={t('marketplace.channelManage.sharing.title')}
      >
        <i className="pi pi-share-alt" />
      </button>

      {open && (
        <div className="marketplace-content-sharing__panel" role="menu">
          <div className="marketplace-content-sharing__header">
            {t('marketplace.channelManage.sharing.title')}
          </div>

          <label className="marketplace-content-sharing__option">
            <input
              type="checkbox"
              name="allow_sharing"
              className="marketplace-content-sharing__checkbox"
              checked={permissions.allow_sharing}
              onChange={handleSharingChange}
              disabled={isUpdating}
            />
            <span className="marketplace-content-sharing__label">
              {t('marketplace.channelManage.sharing.allowInChannels')}
            </span>
          </label>

          <label className="marketplace-content-sharing__option">
            <input
              type="checkbox"
              name="allow_cloning"
              className="marketplace-content-sharing__checkbox"
              checked={permissions.allow_cloning}
              onChange={handleSharingChange}
              disabled={isUpdating}
            />
            <span className="marketplace-content-sharing__label">
              {t('marketplace.channelManage.sharing.allowForking')}
            </span>
          </label>

          <div className="marketplace-content-sharing__attribution">
            <i className="pi pi-info-circle" />
            <span>{t('marketplace.channelManage.sharing.attribution')}</span>
          </div>

          {saveError && (
            <div className="marketplace-content-sharing__error">{saveError}</div>
          )}

          {isUpdating && (
            <div className="marketplace-content-sharing__updating">
              <i className="pi pi-spin pi-spinner" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContentSharingDropdownMolecule;
